import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DialogsService } from '../../../core/services/dialog.service';
import { UploadDAO } from '../../../core/services/dao/uploadDAO';
import { CropperSettings, ImageCropperComponent } from 'ngx-img-cropper';
import { VesselDAO } from '../../../core/services/dao/vesselDAO';
import { VesselImageDAO } from '../../../core/services/dao/vesselImageDAO';
import { VesselImage } from 'anchor-shared/lib/model/entity/vesselImage';
import { OrderVesselImagesByPipe } from '../pipes/order-vessel-images-by.pipe';
import { UserDAO } from '../../../core/services/dao/userDAO';
import { HttpClient } from '@angular/common/http';
import {
  BucketUploadResponse,
  getFileNameWithExtension,
} from '../../../core/pure-functions/upload/image-upload-helper';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { Subject } from 'rxjs/Rx';

@Component({
  selector: 'app-photo-list',
  templateUrl: './photo-list.component.html',
  styleUrls: ['./photo-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PhotoListComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  @Input() paramId: number;

  @Input() keyPrefix: string;

  @Input() vesselForm: boolean;

  @Input() disabled: boolean;

  @Input() s3path: string;

  @Input() userId: number;

  @Input() vesselId: number;

  @Input() maxFiles: number;

  @Input() primaryUrl = '';

  @Input()
  vesselImages: VesselImage[] = [];

  @Input()
  refreshSortOrder: Subject<VesselImage[]>;

  @Output()
  primaryUrlChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  sortOrderChange: EventEmitter<VesselImage> = new EventEmitter<VesselImage>();

  public fileError = '';

  @ViewChild('cropper')
  cropper: ImageCropperComponent;

  filename: string;
  image: any;
  hasFiles: boolean;
  isAdmin = false;
  subs: Subscription[] = [];
  files: VesselImage[] = [];

  constructor(
    public dialog: DialogsService,
    public uploadDAO: UploadDAO,
    public vesselImageDao: VesselImageDAO,
    public vesselDao: VesselDAO,
    public userDAO: UserDAO,
    private orderVesselImagesByPipe: OrderVesselImagesByPipe,
  ) {
    this.userDAO.getCurrentUser().roles.map((role) => {
      if (role.adminAccess) {
        this.isAdmin = true;
      }
    });
  }

  ngOnInit() {
    this.vesselImages = this.orderVesselImagesByPipe.transform(
      this.vesselImages,
      'sortOrder',
    );
    if (this.vesselImages && this.vesselImages.length > 0) {
      this.hasFiles = true;
    }
    const listener = this.refreshSortOrder
      .asObservable()
      .subscribe((newImages: VesselImage[]) => {
        if (newImages) {
          this.vesselImages = this.saveSortOrder(newImages);
          this.setFirstImageAsFeatured();
        }
      });
    this.subs.push(listener);
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  sortOrderChanged(vesselImage: VesselImage): void {
    this.sortOrderChange.emit(vesselImage);
  }

  setFirstImageAsFeatured() {
    const primaryVesselImage = this.vesselImages.filter(
      (img) => img.sortOrder === 1,
    )[0];
    if (primaryVesselImage && !primaryVesselImage.needsCrop) {
      this.primaryUrl = primaryVesselImage.url;
      this.primaryUrlChange.emit(this.primaryUrl);
    } else {
      this.primaryUrl = '';
      this.primaryUrlChange.emit(this.primaryUrl);
    }
  }

  delete(image: VesselImage, index: number) {
    const ref = this.dialog.VesselImageDelete();
    ref.subscribe((res) => {
      if (res) {
        this.vesselImageDao.serverDelete(image).subscribe(
          (result) => {
            console.log(result);
          },
          (err) => console.error(err),
        );

        this.uploadDAO
          .delete(
            getFileNameWithExtension(image.url),
            this.paramId,
            this.s3path,
          )
          .subscribe(
            (result) => {
              const segments = getFileNameWithExtension(image.url).split('.');
              segments.pop();
              segments.push('thumb');
              this.uploadDAO
                .delete(segments.join('.'), this.paramId, this.s3path)
                .subscribe(
                  () => {
                    this.vesselImages.splice(index, 1);
                    this.saveSortOrder(this.vesselImages);
                    this.setFirstImageAsFeatured();
                  },
                  (err) => {
                    console.error(err);
                  },
                );
            },
            (err) => {
              console.error(err);
            },
          );
      }
    });
  }

  drop(event: any): void {
    this.vesselImages = this.changeSortOrder(
      this.vesselImages,
      event.previousIndex,
      event.currentIndex,
    );
    this.setFirstImageAsFeatured();
  }

  getFileNameWithExtension(fileUrl: string) {
    return getFileNameWithExtension(fileUrl);
  }

  changeSortOrder(
    vesselImages: VesselImage[],
    old_index: number,
    new_index: number,
  ): VesselImage[] {
    if (new_index >= vesselImages.length) {
      let k = new_index - vesselImages.length + 1;
      while (k--) {
        vesselImages.push(undefined);
      }
    }
    vesselImages.splice(new_index, 0, vesselImages.splice(old_index, 1)[0]);
    vesselImages = this.saveSortOrder(vesselImages);
    return vesselImages;
  }

  openCropper(vesselImage: VesselImage) {
    this.dialog
      .ImageCropper(vesselImage, this.vesselId, vesselImage.id)
      .subscribe((uploadResponses: BucketUploadResponse[]) => {
        if (uploadResponses) {
          vesselImage.url = uploadResponses[0].url;
          vesselImage.resourceKey = uploadResponses[0].key;
          vesselImage.thumbUrl = uploadResponses[0].url;
          vesselImage.thumbKey = uploadResponses[0].key;
          this.setFirstImageAsFeatured();
        }
      });
  }

  private saveSortOrder(vesselImages: VesselImage[]): VesselImage[] {
    const goodImages = vesselImages.filter(
      (image) => image.needsCrop === false && image.sortOrder,
    );
    const imagesToAssignSortOrder = vesselImages.filter(
      (image) => image.needsCrop === false && image.sortOrder === null,
    );
    const badImages = vesselImages.filter((image) => image.needsCrop === true);
    let currentMax = Math.max.apply(
      Math,
      vesselImages.map((i) => i.sortOrder),
    );
    if (!currentMax) {
      currentMax = 0;
    }
    goodImages.forEach((photo, index) => {
      photo.sortOrder = index + 1;
      this.sortOrderChanged(photo);
    });
    imagesToAssignSortOrder.forEach((photo) => {
      photo.sortOrder = ++currentMax;
      this.sortOrderChanged(photo);
    });
    return this.orderVesselImagesByPipe.transform(
      goodImages.concat(imagesToAssignSortOrder).concat(badImages),
      'sortOrder',
    );
  }

  downloadFile(index: number) {
    const key =
      this.keyPrefix + this.getFileNameWithExtension(this.files[index].url);
    this.uploadDAO.downloadFile(key, this.paramId).subscribe(
      (result) => {
        let type;
        const ext = this.files[index].url.split('.').pop();
        if (ext === 'pdf') {
          type = 'application/pdf';
        } else if (ext === 'jpg') {
          type = 'image/jpeg';
        } else if (ext === 'png') {
          type = 'image/png';
        } else {
          type = '';
        }
        const file = new File([result], this.files[index].url, {
          type: type,
        });
        // FileSaver.saveAs(file);
      },
      (err) => {
        console.error(err);
      },
    );
  }
}
